import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { DoughHydrationCalculatorClient } from './ui/DoughHydrationCalculatorClient';

interface DoughHydrationCalculatorProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const DoughHydrationCalculator: React.FC<DoughHydrationCalculatorProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Free Dough Hydration Calculator"
            description="Calculate the exact hydration percentage of your bread dough with our free tool. Perfect for bakers looking to control texture and crumb for artisan breads."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="dough-hydration"
        >
            <DoughHydrationCalculatorClient />
            <DoughHydrationCalculatorContent />
        </ToolContainer>
    );
};

const DoughHydrationCalculatorContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border">
        {/* SEO Intro Section */}
        <div className="prose prose-pink max-w-none text-brand-text-body">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Master High Hydration Dough</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Hydration is the single most important factor in bread baking. It determines whether you get a dense, tight crumb (low hydration) or an open, airy structure with large irregular holes (<strong>high hydration</strong>). Sourdough bakers often aim for <strong>75% to 85% hydration</strong> for that perfect artisan loaf. Our calculator accounts for all liquid sources—including milk, eggs, and honey—to give you the true hydration percentage of your dough.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Handle Wet Dough
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Autolyse:</strong> Mix flour and water first and let it sit for 30-60 mins.</li>
                    <li><strong>Wet Hands:</strong> Always wet your hands with water (not flour!) before handling sticky high-hydration doughs.</li>
                    <li><strong>Coil Folds:</strong> Instead of kneading, perform gentle "coil folds" every 30 minutes.</li>
                    <li><strong>Cold Retard:</strong> Shaping cold dough is easier. Let your bulk rise happen, then refrigerate.</li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            What hydration is best for beginners?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Start with <strong>65% to 70%</strong>. This dough is tacky but manageable.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
